import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Check, Loader2, CheckCircle, MessageSquare, FileText, X, Phone, Mail, Send, RefreshCw, Paperclip, Upload, GitBranch, History, FolderPlus, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { atencionClienteAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import { ORIGENES, ESTADOS_CONSULTA } from './constants';
import { formatDate, formatDateTime } from './utils';
import { descargarFichero } from '../../lib/auth';

const ConsultaDetailDialog = ({ consulta, isOpen, onClose, plantillas, onUpdate, config }) => {
  const [respuestaForm, setRespuestaForm] = useState({
    destinatario_email: '',
    asunto: '',
    cuerpo: '',
    plantilla_id: null,
    adjuntos: []
  });
  const [enviando, setEnviando] = useState(false);
  const [showRespuestaForm, setShowRespuestaForm] = useState(false);
  const [showRespuestaGestionForm, setShowRespuestaGestionForm] = useState(false);
  const [showSeguimientoForm, setShowSeguimientoForm] = useState(false);
  const [showCrearActuacion, setShowCrearActuacion] = useState(false);
  const [showFinalizarConsulta, setShowFinalizarConsulta] = useState(false);
  const [adjuntosDisponibles, setAdjuntosDisponibles] = useState([]);
  const [adjuntosConsulta, setAdjuntosConsulta] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [crearActuacionForm, setCrearActuacionForm] = useState({ titulo: '', observaciones_adicionales: '' });
  const [seguimientoForm, setSeguimientoForm] = useState({ origen: 'telefono', asunto: '', descripcion_consulta: '', prioridad: 'media' });
  const [respuestaGestionForm, setRespuestaGestionForm] = useState({
    tipo_respuesta: 'telefono',
    telefono_contacto: '',
    duracion_llamada: '',
    notas_gestion: ''
  });
  const [finalizarForm, setFinalizarForm] = useState({ crear_actuacion: true, notas_cierre: '' });
  const [editingRespuesta, setEditingRespuesta] = useState(null);
  const [editRespuestaForm, setEditRespuestaForm] = useState({ cuerpo: '', telefono_contacto: '', duracion_llamada: '', asunto: '' });

  useEffect(() => {
    if (consulta) {
      setRespuestaForm({
        destinatario_email: consulta.cliente_email || '',
        asunto: `Re: ${consulta.asunto || ''}`,
        cuerpo: '',
        plantilla_id: null,
        adjuntos: []
      });
      setRespuestaGestionForm({
        tipo_respuesta: 'telefono',
        telefono_contacto: consulta.cliente_telefono || '',
        duracion_llamada: '',
        notas_gestion: ''
      });
      loadAdjuntos();
      loadAdjuntosConsulta();
    }
  }, [consulta]);

  const loadAdjuntos = async () => {
    if (!consulta) return;
    try {
      const res = await atencionClienteAPI.listarAdjuntos(consulta.id);
      setAdjuntosDisponibles(res.data);
    } catch (error) {
      console.error('Error cargando adjuntos:', error);
    }
  };

  const loadAdjuntosConsulta = async () => {
    if (!consulta) return;
    try {
      const res = await atencionClienteAPI.listarAdjuntosConsulta(consulta.id);
      setAdjuntosConsulta(res.data || []);
    } catch (error) {
      console.error('Error cargando adjuntos de consulta:', error);
      setAdjuntosConsulta([]);
    }
  };

  const handleUploadAdjunto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await atencionClienteAPI.subirAdjunto(consulta.id, formData);
      toast({ title: 'Archivo subido', description: file.name });
      loadAdjuntos();
      // Añadir a la lista de adjuntos del email
      setRespuestaForm(prev => ({
        ...prev,
        adjuntos: [...prev.adjuntos, res.data.path]
      }));
    } catch (error) {
      toast({ title: 'Error al subir archivo', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const toggleAdjunto = (path) => {
    setRespuestaForm(prev => ({
      ...prev,
      adjuntos: prev.adjuntos.includes(path)
        ? prev.adjuntos.filter(p => p !== path)
        : [...prev.adjuntos, path]
    }));
  };

  const handlePlantillaSelect = (plantillaId) => {
    const plantilla = plantillas.find(p => p.id === parseInt(plantillaId));
    if (plantilla) {
      // Reemplazar variables
      let cuerpo = plantilla.cuerpo;
      cuerpo = cuerpo.replace(/\{\{cliente_nombre\}\}/g, `${consulta?.cliente_nombre || ''} ${consulta?.cliente_apellidos || ''}`);
      cuerpo = cuerpo.replace(/\{\{expediente_numero\}\}/g, consulta?.expediente_numero || 'N/A');
      
      setRespuestaForm({
        ...respuestaForm,
        asunto: plantilla.asunto,
        cuerpo: cuerpo,
        plantilla_id: plantilla.id
      });
    }
  };

  const handleEnviarRespuesta = async () => {
    if (!respuestaForm.destinatario_email || !respuestaForm.cuerpo) {
      toast({ title: 'Error', description: 'Complete el email y la respuesta', variant: 'destructive' });
      return;
    }

    setEnviando(true);
    try {
      const res = await atencionClienteAPI.createRespuesta(consulta.id, {
        ...respuestaForm,
        enviar_ahora: true
      });
      
      if (res.data.enviado) {
        const adjCount = res.data.adjuntos_count || 0;
        toast({ 
          title: '¡Email enviado!', 
          description: `La respuesta se ha enviado correctamente${adjCount > 0 ? ` con ${adjCount} adjunto(s)` : ''}` 
        });
        setShowRespuestaForm(false);
        setRespuestaForm({ destinatario_email: consulta.cliente_email || '', asunto: '', cuerpo: '', plantilla_id: null, adjuntos: [] });
        onUpdate();
      } else {
        toast({ 
          title: 'Error al enviar', 
          description: res.data.error || 'No se pudo enviar el email. Verifique la configuración SMTP.',
          variant: 'destructive' 
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo enviar la respuesta', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleCrearSeguimiento = async () => {
    if (!seguimientoForm.asunto || !seguimientoForm.descripcion_consulta) {
      toast({ title: 'Error', description: 'Complete el asunto y descripción', variant: 'destructive' });
      return;
    }
    
    setEnviando(true);
    try {
      const res = await atencionClienteAPI.crearSeguimiento(consulta.id, seguimientoForm);
      toast({ title: 'Seguimiento creado', description: `Seguimiento #${res.data.numero_seguimiento} añadido al hilo` });
      setShowSeguimientoForm(false);
      setSeguimientoForm({ origen: 'telefono', asunto: '', descripcion_consulta: '', prioridad: 'media' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error al crear seguimiento', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleCrearActuacion = async () => {
    if (!consulta.expediente_id) {
      toast({ title: 'Error', description: 'La consulta no tiene expediente asociado', variant: 'destructive' });
      return;
    }
    
    setEnviando(true);
    try {
      const res = await atencionClienteAPI.crearActuacion(consulta.id, crearActuacionForm);
      toast({ 
        title: '¡Actuación creada!', 
        description: `Actuación añadida al expediente ${res.data.expediente_numero}` 
      });
      setShowCrearActuacion(false);
      onUpdate();
    } catch (error) {
      const msg = error.response?.data?.detail || 'No se pudo crear la actuación';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleReenviar = async (respuestaId) => {
    try {
      const res = await atencionClienteAPI.reenviarRespuesta(consulta.id, respuestaId);
      if (res.data.enviado) {
        toast({ title: 'Email reenviado correctamente' });
        onUpdate();
      } else {
        toast({ title: 'Error', description: res.data.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error al reenviar', variant: 'destructive' });
    }
  };

  const handleStartEditRespuesta = (resp) => {
    setEditingRespuesta(resp.id);
    setEditRespuestaForm({
      cuerpo: resp.cuerpo || '',
      telefono_contacto: resp.telefono_contacto || '',
      duracion_llamada: resp.duracion_llamada || '',
      asunto: resp.asunto || ''
    });
  };

  const handleSaveEditRespuesta = async () => {
    if (!editRespuestaForm.cuerpo.trim()) {
      toast({ title: 'Error', description: 'El contenido no puede estar vacío', variant: 'destructive' });
      return;
    }
    setEnviando(true);
    try {
      await atencionClienteAPI.updateRespuesta(consulta.id, editingRespuesta, editRespuestaForm);
      toast({ title: 'Gestión actualizada correctamente' });
      setEditingRespuesta(null);
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo actualizar la gestión', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleDeleteRespuesta = async (respuestaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta gestión?')) return;
    try {
      await atencionClienteAPI.deleteRespuesta(consulta.id, respuestaId);
      toast({ title: 'Gestión eliminada correctamente' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar la gestión', variant: 'destructive' });
    }
  };

  const handleRespuestaGestion = async () => {
    if (!respuestaGestionForm.notas_gestion.trim()) {
      toast({ title: 'Error', description: 'Ingrese las notas de la gestión realizada', variant: 'destructive' });
      return;
    }

    setEnviando(true);
    try {
      await atencionClienteAPI.createRespuesta(consulta.id, {
        tipo_respuesta: respuestaGestionForm.tipo_respuesta,
        cuerpo: respuestaGestionForm.notas_gestion,
        telefono_contacto: respuestaGestionForm.telefono_contacto,
        duracion_llamada: respuestaGestionForm.duracion_llamada,
        enviar_ahora: false
      });
      
      toast({ 
        title: '✅ Gestión registrada', 
        description: `Respuesta por ${respuestaGestionForm.tipo_respuesta === 'telefono' ? 'teléfono' : 'WhatsApp'} guardada correctamente` 
      });
      setShowRespuestaGestionForm(false);
      setRespuestaGestionForm({
        tipo_respuesta: 'telefono',
        telefono_contacto: consulta.cliente_telefono || '',
        duracion_llamada: '',
        notas_gestion: ''
      });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar la gestión', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleFinalizarConsulta = async () => {
    setEnviando(true);
    try {
      await atencionClienteAPI.finalizarConsulta(consulta.id, {
        crear_actuacion: Boolean(finalizarForm.crear_actuacion && consulta.expediente_id),
        notas_cierre: finalizarForm.notas_cierre
      });
      
      toast({ 
        title: '✅ Consulta finalizada', 
        description: finalizarForm.crear_actuacion && consulta.expediente_id 
          ? 'Se ha cerrado la consulta y creado la actuación en el expediente'
          : 'La consulta ha sido cerrada'
      });
      setShowFinalizarConsulta(false);
      onUpdate();
      onClose();
    } catch (error) {
      let msg = error.response?.data?.detail || 'No se pudo finalizar la consulta'; if(typeof msg==='object')msg=JSON.stringify(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleEliminarAdjuntoConsulta = async (adjuntoPath) => {
    if (!window.confirm('¿Eliminar este archivo adjunto?')) return;
    try {
      await atencionClienteAPI.eliminarAdjuntoConsulta(consulta.id, adjuntoPath);
      toast({ title: 'Archivo eliminado' });
      loadAdjuntosConsulta();
      onUpdate();
    } catch (error) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  if (!consulta) return null;

  const origenConfig = ORIGENES[consulta.origen] || ORIGENES.telefono;
  const estadoConfig = ESTADOS_CONSULTA[consulta.estado] || ESTADOS_CONSULTA.en_proceso;
  const OrigenIcon = origenConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <OrigenIcon className="w-5 h-5" />
            Detalle de Consulta
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info del cliente */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    {consulta.cliente_nombre} {consulta.cliente_apellidos}
                  </h3>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                    {consulta.cliente_telefono && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {consulta.cliente_telefono}
                      </span>
                    )}
                    {consulta.cliente_email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {consulta.cliente_email}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge className={estadoConfig.color}>{estadoConfig.nombre}</Badge>
                  <Badge className={origenConfig.color}>{origenConfig.nombre}</Badge>
                </div>
              </div>
              
              {consulta.expediente_numero && (
                <div className="mt-3 p-2 bg-blue-50 rounded flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    Expediente: {consulta.expediente_numero}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Consulta */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Consulta</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium text-gray-900">{consulta.asunto}</p>
              <p className="mt-2 text-gray-700 whitespace-pre-wrap">{consulta.descripcion_consulta}</p>
              <p className="mt-3 text-xs text-gray-500">
                Recibido: {formatDateTime(consulta.created_at)} • Por: {consulta.creado_por_nombre || 'Sistema'}
              </p>
            </CardContent>
          </Card>

          {/* Adjuntos de la consulta */}
          {adjuntosConsulta.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Archivos Adjuntos ({adjuntosConsulta.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {adjuntosConsulta.map((adj, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                      <Paperclip className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{adj.nombre}</span>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          type="button"
                          onClick={() => descargarFichero(
                            `${process.env.REACT_APP_BACKEND_URL}/api/atencion-cliente/consultas/${consulta.id}/adjuntos-consulta/${encodeURIComponent(adj.nombre)}/descargar`,
                            adj.nombre
                          )}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Descargar"
                        >
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleEliminarAdjuntoConsulta(adj.path)}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Respuestas enviadas */}
          {consulta.respuestas && consulta.respuestas.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Gestiones Realizadas ({consulta.respuestas.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                {consulta.respuestas.map((resp) => {
                  const tipoResp = resp.tipo_respuesta || 'email';
                  const isEmail = tipoResp === 'email';
                  const isTelefono = tipoResp === 'telefono';
                  const isWhatsapp = tipoResp === 'whatsapp';
                  const isEditing = editingRespuesta === resp.id;
                  
                  return (
                    <div key={resp.id} className={`p-3 rounded-lg ${
                      isEmail 
                        ? (resp.enviado ? 'bg-green-50' : 'bg-red-50')
                        : isTelefono 
                          ? 'bg-blue-50' 
                          : 'bg-emerald-50'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {isEmail && <Mail className="w-4 h-4 text-blue-600" />}
                          {isTelefono && <Phone className="w-4 h-4 text-blue-600" />}
                          {isWhatsapp && <MessageSquare className="w-4 h-4 text-emerald-600" />}
                          <div>
                            {isEmail ? (
                              <>
                                <p className="font-medium">{resp.asunto}</p>
                                <p className="text-sm text-gray-600">Para: {resp.destinatario_email}</p>
                              </>
                            ) : (
                              <>
                                <p className="font-medium">
                                  {isTelefono ? 'Llamada telefónica' : 'Mensaje WhatsApp'}
                                </p>
                                {resp.telefono_contacto && (
                                  <p className="text-sm text-gray-600">Tel: {resp.telefono_contacto}</p>
                                )}
                                {resp.duracion_llamada && (
                                  <p className="text-xs text-gray-500">Duración: {resp.duracion_llamada} min</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isEmail ? (
                            resp.enviado ? (
                              <Badge className="bg-green-100 text-green-700">
                                <Check className="w-3 h-3 mr-1" /> Enviado
                              </Badge>
                            ) : (
                              <>
                                <Badge className="bg-red-100 text-red-700">Error</Badge>
                                <Button size="sm" variant="outline" onClick={() => handleReenviar(resp.id)}>
                                  <RefreshCw className="w-3 h-3 mr-1" /> Reenviar
                                </Button>
                              </>
                            )
                          ) : (
                            <Badge className={isTelefono ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}>
                              {isTelefono ? '📞 Teléfono' : '💬 WhatsApp'}
                            </Badge>
                          )}
                          {!isEditing && (
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-gray-500 hover:text-blue-600"
                                onClick={(e) => { e.stopPropagation(); handleStartEditRespuesta(resp); }}
                                title="Editar gestión"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-gray-500 hover:text-red-600"
                                onClick={(e) => { e.stopPropagation(); handleDeleteRespuesta(resp.id); }}
                                title="Eliminar gestión"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="mt-2 space-y-2">
                          {(isTelefono || isWhatsapp) && (
                            <div className="flex gap-2">
                              <Input
                                placeholder="Teléfono contacto"
                                value={editRespuestaForm.telefono_contacto}
                                onChange={(e) => setEditRespuestaForm(prev => ({ ...prev, telefono_contacto: e.target.value }))}
                                className="flex-1"
                              />
                              {isTelefono && (
                                <Input
                                  placeholder="Duración (min)"
                                  value={editRespuestaForm.duracion_llamada}
                                  onChange={(e) => setEditRespuestaForm(prev => ({ ...prev, duracion_llamada: e.target.value }))}
                                  className="w-32"
                                />
                              )}
                            </div>
                          )}
                          <Textarea
                            value={editRespuestaForm.cuerpo}
                            onChange={(e) => setEditRespuestaForm(prev => ({ ...prev, cuerpo: e.target.value }))}
                            rows={5}
                            className="w-full"
                          />
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => setEditingRespuesta(null)}>
                              <X className="w-3 h-3 mr-1" /> Cancelar
                            </Button>
                            <Button size="sm" onClick={handleSaveEditRespuesta} disabled={enviando}>
                              <Check className="w-3 h-3 mr-1" /> {enviando ? 'Guardando...' : 'Guardar'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{resp.cuerpo}</p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        {resp.fecha_envio ? formatDateTime(resp.fecha_envio) : formatDateTime(resp.created_at)} • {resp.enviado_por_nombre}
                      </p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Botones de Respuesta */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Button 
              className={`${config.bgColor} hover:opacity-90 text-white`}
              onClick={() => { setShowRespuestaForm(true); setShowRespuestaGestionForm(false); }}
              disabled={!consulta.cliente_email}
            >
              <Mail className="w-4 h-4 mr-2" />
              {consulta.cliente_email ? 'Responder por Email' : 'Sin email'}
            </Button>
            
            <Button 
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => { 
                setShowRespuestaGestionForm(true); 
                setShowRespuestaForm(false); 
                setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'telefono' }));
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Registrar Llamada
            </Button>
            
            <Button 
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => { 
                setShowRespuestaGestionForm(true); 
                setShowRespuestaForm(false); 
                setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'whatsapp' }));
              }}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Registrar WhatsApp
            </Button>
          </div>

          {/* Formulario de respuesta por Email */}
          {showRespuestaForm && (
            <Card className="border-2 border-cyan-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Nueva Respuesta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {plantillas.length > 0 && (
                  <div>
                    <Label>Usar plantilla</Label>
                    <Select onValueChange={handlePlantillaSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar plantilla (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {plantillas.map((p) => (
                          <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Email destinatario</Label>
                  <Input
                    type="email"
                    value={respuestaForm.destinatario_email}
                    onChange={(e) => setRespuestaForm({...respuestaForm, destinatario_email: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={respuestaForm.asunto}
                    onChange={(e) => setRespuestaForm({...respuestaForm, asunto: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Respuesta</Label>
                  <Textarea
                    value={respuestaForm.cuerpo}
                    onChange={(e) => setRespuestaForm({...respuestaForm, cuerpo: e.target.value})}
                    rows={6}
                    placeholder="Escriba la respuesta al cliente..."
                  />
                </div>

                {/* Adjuntos */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Adjuntos
                  </Label>
                  <div className="mt-2 space-y-2">
                    {adjuntosDisponibles.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {adjuntosDisponibles.map((adj, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => toggleAdjunto(adj.path)}
                            className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 transition-colors ${
                              respuestaForm.adjuntos.includes(adj.path) 
                                ? 'bg-cyan-100 text-cyan-700 border-cyan-300 border' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <Paperclip className="w-3 h-3" />
                            {adj.nombre.split('_').slice(2).join('_') || adj.nombre}
                            {respuestaForm.adjuntos.includes(adj.path) && <Check className="w-3 h-3" />}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <Upload className="w-4 h-4" />
                        Subir nuevo archivo
                        <input type="file" className="hidden" onChange={handleUploadAdjunto} disabled={uploading} />
                      </label>
                      {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    {respuestaForm.adjuntos.length > 0 && (
                      <p className="text-xs text-cyan-600">{respuestaForm.adjuntos.length} archivo(s) seleccionado(s) para adjuntar</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowRespuestaForm(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className={`${config.bgColor} hover:opacity-90 text-white`}
                    onClick={handleEnviarRespuesta}
                    disabled={enviando}
                  >
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Enviar Email {respuestaForm.adjuntos.length > 0 && `(${respuestaForm.adjuntos.length} adj.)`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario de Gestión por Teléfono/WhatsApp */}
          {showRespuestaGestionForm && (
            <Card className={`border-2 ${respuestaGestionForm.tipo_respuesta === 'telefono' ? 'border-blue-200' : 'border-emerald-200'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {respuestaGestionForm.tipo_respuesta === 'telefono' 
                    ? <><Phone className="w-4 h-4 text-blue-600" /> Registrar Llamada Telefónica</>
                    : <><MessageSquare className="w-4 h-4 text-emerald-600" /> Registrar Gestión WhatsApp</>
                  }
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Registre los detalles de la gestión realizada con el cliente
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={respuestaGestionForm.tipo_respuesta === 'telefono' ? 'default' : 'outline'}
                    onClick={() => setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'telefono' }))}
                    className={respuestaGestionForm.tipo_respuesta === 'telefono' ? 'bg-blue-600' : ''}
                  >
                    <Phone className="w-4 h-4 mr-1" /> Teléfono
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={respuestaGestionForm.tipo_respuesta === 'whatsapp' ? 'default' : 'outline'}
                    onClick={() => setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'whatsapp' }))}
                    className={respuestaGestionForm.tipo_respuesta === 'whatsapp' ? 'bg-emerald-600' : ''}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Teléfono contactado</Label>
                    <Input
                      value={respuestaGestionForm.telefono_contacto}
                      onChange={(e) => setRespuestaGestionForm({...respuestaGestionForm, telefono_contacto: e.target.value})}
                      placeholder="Ej: 612345678"
                    />
                  </div>
                  {respuestaGestionForm.tipo_respuesta === 'telefono' && (
                    <div>
                      <Label>Duración (minutos)</Label>
                      <Input
                        type="number"
                        value={respuestaGestionForm.duracion_llamada}
                        onChange={(e) => setRespuestaGestionForm({...respuestaGestionForm, duracion_llamada: e.target.value})}
                        placeholder="Ej: 15"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label>Notas de la gestión *</Label>
                  <Textarea
                    value={respuestaGestionForm.notas_gestion}
                    onChange={(e) => setRespuestaGestionForm({...respuestaGestionForm, notas_gestion: e.target.value})}
                    rows={5}
                    placeholder={respuestaGestionForm.tipo_respuesta === 'telefono' 
                      ? "Describa el contenido de la llamada, acuerdos alcanzados, próximos pasos..."
                      : "Describa el contenido del mensaje de WhatsApp, respuesta del cliente..."
                    }
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowRespuestaGestionForm(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className={respuestaGestionForm.tipo_respuesta === 'telefono' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                    onClick={handleRespuestaGestion}
                    disabled={enviando}
                  >
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {respuestaGestionForm.tipo_respuesta === 'telefono' 
                      ? <><Phone className="w-4 h-4 mr-2" /> Guardar Llamada</>
                      : <><MessageSquare className="w-4 h-4 mr-2" /> Guardar WhatsApp</>
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Botones de acción adicionales */}
          <div className="flex flex-wrap gap-2">
            {/* Botón Seguimiento */}
            <Button 
              variant="outline" 
              onClick={() => setShowSeguimientoForm(!showSeguimientoForm)}
              className="flex-1"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              {showSeguimientoForm ? 'Cancelar' : 'Nueva Consulta de Seguimiento'}
            </Button>
            
            {/* Botón Finalizar Consulta */}
            {consulta.estado !== 'completado' && (
              <Button 
                variant="outline" 
                onClick={() => setShowFinalizarConsulta(!showFinalizarConsulta)}
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {showFinalizarConsulta ? 'Cancelar' : 'Finalizar Consulta'}
              </Button>
            )}
            
            {/* Botón Crear Actuación (solo si no finalizamos con actuación automática) */}
            {consulta.expediente_id && !consulta.actuacion_creada && consulta.estado !== 'completado' && (
              <Button 
                variant="outline" 
                onClick={() => setShowCrearActuacion(!showCrearActuacion)}
                className="flex-1"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Crear Actuación
              </Button>
            )}
            
            {consulta.actuacion_creada && (
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" /> Actuación creada
              </Badge>
            )}
            
            {consulta.estado === 'completado' && (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" /> Consulta completada
              </Badge>
            )}
          </div>

          {/* Formulario Finalizar Consulta */}
          {showFinalizarConsulta && (
            <Card className="border-2 border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <CheckCircle className="w-4 h-4" />
                  Finalizar Consulta
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Marcar esta consulta como resuelta y cerrada
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {consulta.expediente_id && !consulta.actuacion_creada && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
                    <input
                      type="checkbox"
                      id="crear_actuacion_auto"
                      checked={finalizarForm.crear_actuacion}
                      onChange={(e) => setFinalizarForm({...finalizarForm, crear_actuacion: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="crear_actuacion_auto" className="text-sm">
                      <span className="font-medium">Crear actuación automática</span> en el expediente {consulta.expediente_numero}
                    </label>
                  </div>
                )}

                <div>
                  <Label>Notas de cierre (opcional)</Label>
                  <Textarea
                    value={finalizarForm.notas_cierre}
                    onChange={(e) => setFinalizarForm({...finalizarForm, notas_cierre: e.target.value})}
                    rows={3}
                    placeholder="Añada notas sobre la resolución de esta consulta..."
                  />
                </div>

                <div className="bg-amber-50 p-3 rounded text-sm text-amber-800">
                  <p className="font-medium">Al finalizar:</p>
                  <ul className="list-disc list-inside mt-1 text-xs">
                    <li>El estado de la consulta cambiará a &quot;Cerrado&quot;</li>
                    {finalizarForm.crear_actuacion && consulta.expediente_id && (
                      <li>Se creará una actuación con el resumen completo de la tramitación</li>
                    )}
                  </ul>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowFinalizarConsulta(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleFinalizarConsulta}
                    disabled={enviando}
                  >
                    {enviando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finalizar y Cerrar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario Seguimiento */}
          {showSeguimientoForm && (
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Nueva Consulta de Seguimiento
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Esta consulta quedará vinculada al hilo de tramitación original
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Origen</Label>
                  <div className="flex gap-2 mt-1">
                    {Object.entries(ORIGENES).map(([key, val]) => {
                      const OrIcon = val.icon;
                      return (
                        <Button
                          key={key}
                          type="button"
                          size="sm"
                          variant={seguimientoForm.origen === key ? 'default' : 'outline'}
                          onClick={() => setSeguimientoForm({...seguimientoForm, origen: key})}
                        >
                          <OrIcon className="w-4 h-4 mr-1" />
                          {val.nombre}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <Label>Asunto de la nueva consulta</Label>
                  <Input
                    value={seguimientoForm.asunto}
                    onChange={(e) => setSeguimientoForm({...seguimientoForm, asunto: e.target.value})}
                    placeholder="Ej: Seguimiento - Información adicional solicitada"
                  />
                </div>
                
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={seguimientoForm.descripcion_consulta}
                    onChange={(e) => setSeguimientoForm({...seguimientoForm, descripcion_consulta: e.target.value})}
                    rows={4}
                    placeholder="Detalle de la nueva consulta del cliente..."
                  />
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSeguimientoForm(false)}>Cancelar</Button>
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={handleCrearSeguimiento}
                    disabled={enviando}
                  >
                    {enviando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <GitBranch className="w-4 h-4 mr-2" />
                    Crear Seguimiento
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario Crear Actuación */}
          {showCrearActuacion && (
            <Card className="border-2 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderPlus className="w-4 h-4" />
                  Crear Actuación en Expediente
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Se creará una actuación en el expediente {consulta.expediente_numero} con el resumen de toda la tramitación
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Título de la actuación (opcional)</Label>
                  <Input
                    value={crearActuacionForm.titulo}
                    onChange={(e) => setCrearActuacionForm({...crearActuacionForm, titulo: e.target.value})}
                    placeholder={`Atención al cliente: ${consulta.asunto?.substring(0, 50)}...`}
                  />
                </div>
                
                <div>
                  <Label>Observaciones adicionales (opcional)</Label>
                  <Textarea
                    value={crearActuacionForm.observaciones_adicionales}
                    onChange={(e) => setCrearActuacionForm({...crearActuacionForm, observaciones_adicionales: e.target.value})}
                    rows={3}
                    placeholder="Añada observaciones adicionales si lo desea..."
                  />
                </div>
                
                <div className="bg-amber-50 p-3 rounded text-sm text-amber-800">
                  <p className="font-medium">La actuación incluirá:</p>
                  <ul className="list-disc list-inside mt-1 text-xs">
                    <li>Datos del cliente</li>
                    <li>Consulta original y descripción</li>
                    <li>Todas las respuestas enviadas ({consulta.respuestas?.length || 0})</li>
                    <li>Todos los seguimientos ({consulta.seguimientos?.length || 0})</li>
                  </ul>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCrearActuacion(false)}>Cancelar</Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleCrearActuacion}
                    disabled={enviando}
                  >
                    {enviando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Crear Actuación
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Información del hilo */}
          {(consulta.consulta_padre_id || (consulta.seguimientos && consulta.seguimientos.length > 0)) && (
            <Card className="bg-purple-50 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Hilo de Tramitación
                </CardTitle>
              </CardHeader>
              <CardContent>
                {consulta.consulta_padre && (
                  <div className="mb-3 p-2 bg-white rounded">
                    <p className="text-sm font-medium">Consulta original:</p>
                    <p className="text-sm text-gray-600">{consulta.consulta_padre.asunto}</p>
                  </div>
                )}
                
                {consulta.seguimientos && consulta.seguimientos.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Seguimientos ({consulta.seguimientos.length}):</p>
                    {consulta.seguimientos.map((seg) => (
                      <div key={seg.id} className="p-2 bg-white rounded text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-700">#{seg.numero_seguimiento}</Badge>
                          <span className="font-medium">{seg.asunto}</span>
                          <Badge className={ESTADOS_CONSULTA[seg.estado]?.color || ''}>{seg.estado}</Badge>
                        </div>
                        <p className="text-gray-600 text-xs mt-1">{formatDateTime(seg.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


export default ConsultaDetailDialog;
